import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, MailCheck, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

const inputCls =
  "w-full bg-[#0a0a0a] border border-white/15 rounded-none px-4 py-3.5 font-sans text-sm text-white placeholder:font-mono placeholder:text-white/40 placeholder:text-xs placeholder:uppercase placeholder:tracking-wider focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

function GoogleButton({ label }) {
  const { loginWithGoogle } = useAuth();
  return (
    <div className="pt-1">
      <div className="flex items-center gap-3 my-4">
        <span className="h-px flex-1 bg-white/15" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">or</span>
        <span className="h-px flex-1 bg-white/15" />
      </div>
      <button
        type="button"
        onClick={loginWithGoogle}
        data-testid="google-signin-btn"
        className="w-full flex items-center justify-center gap-3 bg-white text-[#111] py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-white/90 transition-colors active:scale-[0.99]"
      >
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
          <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
          <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
          <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C41.372 36.023 44 30.5 44 24c0-1.341-.138-2.65-.389-3.917z" />
        </svg>
        {label}
      </button>
    </div>
  );
}

function AuthShell({ title, sub, children, footer }) {
  return (
    <div className="dark min-h-screen bg-background text-foreground grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/10 relative overflow-hidden grain">
        <Logo to="/" size="h-12" className="relative z-10" testId="auth-logo" />
        <div className="relative z-10">
          <h2 className="font-display font-black uppercase tracking-tighter text-6xl leading-[0.85] text-transparent [-webkit-text-stroke:1.5px_#f7f7f6]">
            Client<br />Portal
          </h2>
          <p className="font-mono text-xs uppercase tracking-widest text-primary mt-6">Track assets · Chain of custody · Certificates</p>
        </div>
        <p className="font-mono text-[10px] text-white/30 relative z-10">R2v3 · ISO 27001 · NIST 800-88</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-white/50 hover:text-primary mb-8" data-testid="auth-back-home">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <h1 className="font-display font-extrabold text-4xl tracking-tight mb-2">{title}</h1>
          <p className="font-sans text-white/50 text-sm mb-8">{sub}</p>
          {children}
          <div className="mt-6 font-mono text-xs text-white/50">{footer}</div>
        </motion.div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@envirotek.net");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNeedsVerify(false); setResent(false); setLoading(true);
    try {
      await login(email, password);
      navigate("/portal");
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (status === 403) setNeedsVerify(true);
      setError(msg);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email });
      setResent(true);
    } catch { /* enumeration-safe, ignore */ }
  };

  return (
    <AuthShell
      title="Client login"
      sub="Access your asset dashboard and quote history."
      footer={<>No account? <Link to="/register" className="text-primary hover:underline" data-testid="go-register">Create one</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input data-testid="login-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="login-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <p data-testid="login-error" className="font-mono text-xs text-destructive">{error}</p>}
        {needsVerify && (
          resent
            ? <p className="font-mono text-xs text-secondary" data-testid="verify-resent">Verification email sent — check your inbox.</p>
            : <button type="button" onClick={resend} data-testid="resend-verification" className="font-mono text-xs text-primary hover:underline">Resend verification email</button>
        )}
        <button type="submit" disabled={loading} data-testid="login-submit" className="w-full flex items-center justify-center gap-2 bg-primary text-white py-4 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors active:scale-[0.99] disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>
        <div className="flex items-center justify-between pt-2">
          <span className="font-mono text-[11px] text-white/30">Demo: demo@envirotek.net / Demo@2025</span>
          <Link to="/forgot-password" data-testid="forgot-password-link" className="font-mono text-[11px] text-primary hover:underline">Forgot password?</Link>
        </div>
      </form>
      <GoogleButton label="Continue with Google" />
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email: form.email });
      setResent(true);
    } catch { /* ignore */ }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        sub="One more step to activate your account."
        footer={<>Wrong email? <Link to="/register" onClick={() => setSent(false)} className="text-primary hover:underline">Start over</Link></>}
      >
        <div data-testid="verify-sent" className="text-center py-6">
          <MailCheck size={40} className="mx-auto text-primary mb-5" />
          <p className="font-sans text-sm text-white/70 leading-relaxed">
            We sent a verification link to <span className="text-white font-semibold">{form.email}</span>.
            Click it to activate your ENVIROTEK portal account, then sign in.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/login" data-testid="verify-goto-login" className="w-full bg-primary text-white py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors">
              Go to sign in
            </Link>
            {resent
              ? <span className="font-mono text-xs text-secondary" data-testid="register-resent">Sent again — check your inbox.</span>
              : <button type="button" onClick={resend} data-testid="register-resend" className="font-mono text-xs text-white/50 hover:text-primary">Didn't get it? Resend</button>}
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create account"
      sub="Set up your ENVIROTEK client portal in seconds."
      footer={<>Already registered? <Link to="/login" className="text-primary hover:underline" data-testid="go-login">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input data-testid="register-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
        <input data-testid="register-email" type="email" className={inputCls} placeholder="Work email" value={form.email} onChange={set("email")} />
        <input data-testid="register-company" className={inputCls} placeholder="Company" value={form.company} onChange={set("company")} />
        <input data-testid="register-password" type="password" className={inputCls} placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} />
        {error && <p data-testid="register-error" className="font-mono text-xs text-destructive">{error}</p>}
        <button type="submit" disabled={loading} data-testid="register-submit" className="w-full flex items-center justify-center gap-2 bg-primary text-white py-4 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors active:scale-[0.99] disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
        </button>
      </form>
      <GoogleButton label="Sign up with Google" />
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true); // enumeration-safe: same result regardless
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      title="Reset password"
      sub="We'll email you a secure link to set a new password."
      footer={<><Link to="/login" className="text-primary hover:underline" data-testid="forgot-back-login">Back to sign in</Link></>}
    >
      {sent ? (
        <div data-testid="forgot-sent" className="text-center py-6">
          <MailCheck size={40} className="mx-auto text-primary mb-5" />
          <p className="font-sans text-sm text-white/70 leading-relaxed">
            If an account exists for <span className="text-white font-semibold">{email}</span>, a password
            reset link is on its way. The link expires in 1 hour.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input data-testid="forgot-email" type="email" required className={inputCls} placeholder="Your account email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full flex items-center justify-center gap-2 bg-primary text-white py-4 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors active:scale-[0.99] disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Send reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      title="Set a new password"
      sub="Choose a strong password for your account."
      footer={<><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></>}
    >
      {!token ? (
        <p className="font-mono text-xs text-destructive" data-testid="reset-no-token">Invalid reset link.</p>
      ) : done ? (
        <div data-testid="reset-done" className="text-center py-6">
          <CheckCircle2 size={40} className="mx-auto text-secondary mb-4" />
          <p className="font-sans text-sm text-white/70">Password updated. Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <input data-testid="reset-password" type="password" className={inputCls} placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <input data-testid="reset-confirm" type="password" className={inputCls} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {error && <p data-testid="reset-error" className="font-mono text-xs text-destructive">{error}</p>}
          <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full flex items-center justify-center gap-2 bg-primary text-white py-4 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors active:scale-[0.99] disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setMsg("Invalid verification link."); return; }
    let active = true;
    api.post("/auth/verify-email", { token })
      .then((r) => { if (active) { setState("success"); setMsg(r.data.message || "Email verified."); } })
      .catch((err) => { if (active) { setState("error"); setMsg(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed."); } });
    return () => { active = false; };
  }, [token]);

  return (
    <AuthShell
      title="Email verification"
      sub="Activating your ENVIROTEK account."
      footer={<><Link to="/login" className="text-primary hover:underline" data-testid="verify-back-login">Go to sign in</Link></>}
    >
      <div data-testid="verify-email-page" className="text-center py-6">
        {state === "verifying" && <><Loader2 size={36} className="mx-auto text-primary animate-spin mb-4" /><p className="font-mono text-xs uppercase tracking-widest text-white/50">Verifying…</p></>}
        {state === "success" && <><CheckCircle2 size={40} className="mx-auto text-secondary mb-4" data-testid="verify-success" /><p className="font-sans text-sm text-white/70">{msg}</p><Link to="/login" className="inline-block mt-6 bg-primary text-white py-3 px-6 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors">Sign in</Link></>}
        {state === "error" && <><XCircle size={40} className="mx-auto text-destructive mb-4" data-testid="verify-error" /><p className="font-sans text-sm text-white/70">{msg}</p></>}
      </div>
    </AuthShell>
  );
}
